import { Button, Mentions, Modal, Popover } from "antd";
import EmojiPicker from "emoji-picker-react";
import MicrolinkCard from "@microlink/react";
import { UiTextsContext, UserDataContext } from "../../contexts/Contexts";
import {
  newPublicationService,
  suggestMentionsService,
} from "../../../infrastructure/services/PublicationService";
import { useContext, useState, useEffect } from "react";
import { AvatarLayout } from "../publications/AvatarLayout";
import { generateGuid } from "../../../shared/utils/misc/generate";
import { CloseOutlined } from "@ant-design/icons";
import { ModalLocation } from "./ModalLocation";
import { showWarningMessage } from "../utils/messages";
import { TextLengthIndicator } from "../publications/TextLengthIndicator";
import { dispatchPublicationChanged } from "../publications/publicationRealtimeEvents";
import {
  TEXT_LIMITS,
  getTextValidationState,
} from "../../../shared/constants/textLimits";
import { searchUserService } from "../../../infrastructure/services/UsersService";
import {
  DEFAULT_PROFILE_IMAGE,
  useMentionSuggestionPhotos,
} from "../../../infrastructure/hooks/useMentionSuggestionPhotos";
import "../../pages/templates/templates.css";
import "../../styles/NewModalPost.css";
import "../../styles/publications.css";

export const ModalPost = ({ open, setOpen, onPublished }) => {
  const { userData } = useContext(UserDataContext);
  const { texts } = useContext(UiTextsContext);

  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendImg, setSendImg] = useState(false);
  const [locationModal, setLocationModal] = useState(false);
  const [filename, setFilename] = useState("");
  const [filetype, setFiletype] = useState("");
  const [photoID, setPhotoID] = useState("");
  const [base64, setBase64] = useState("");
  const [location, setLocation] = useState("");
  const [detectedUrl, setDetectedUrl] = useState(null);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const getMentionPhoto = useMentionSuggestionPhotos(filteredSuggestions);
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [currentMentionSearch, setCurrentMentionSearch] = useState("");
  const [currentMentionPrefix, setCurrentMentionPrefix] = useState("@");
  const [hashtagSuggestions, setHashtagSuggestions] = useState([]);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadSuggestions = async () => {
      try {
        if (currentMentionPrefix === "#") {
          const criteria = `#${currentMentionSearch.trim().replace(/^#/, "")}`;
          const data = await searchUserService({
            IdUsuario: userData?.idUsuario,
            Criteria: criteria,
          });
          if (cancelled) return;
          setHashtagSuggestions(
            (Array.isArray(data) ? data : [])
              .map(
                (result) =>
                  result?.nombres ??
                  result?.Nombres ??
                  result?.nombre ??
                  result?.Nombre ??
                  result?.hashtag,
              )
              .filter((name) => typeof name === "string" && name.startsWith("#")),
          );
          return;
        }

        if (!currentMentionSearch) {
          setFilteredSuggestions([]);
          return;
        }
        const req = {
          IdUsuario: userData?.idUsuario,
          Nickname: currentMentionSearch,
        };
        const data = await suggestMentionsService(req);
        if (!cancelled && data) {
          setFilteredSuggestions(data);
        }
      } catch (error) {
        console.error("Error loading suggestions", error);
      }
    };
    if (userData?.idUsuario) loadSuggestions();
    return () => {
      cancelled = true;
    };
  }, [currentMentionSearch, currentMentionPrefix, userData?.idUsuario]);

  const onChangeInput = (text) => {
    if (submitError) setSubmitError("");
    setInputText(text);
    const mentionMatches = text.match(/@\w+/g) || [];
    const mentionedIds = mentionMatches
      .map((mention) => {
        const user = filteredSuggestions.find(
          (u) => u.nickName.toLowerCase() === mention.toLowerCase(),
        );
        return user?.idUsuario;
      })
      .filter(Boolean);
    setMentionedUsers(mentionedIds);
  };

  const onOkUpload = async () => {
    const validation = getTextValidationState(inputText);
    const canSubmit = sendImg ? !validation.isTooLong : validation.isValid;
    if (!canSubmit) return;

    setLoading(true);
    try {
      const request = buildPostReq();
      const publicationResponse = await newPublicationService(request);
      dispatchPublicationChanged({
        kind: "publication-created",
        senderId: userData?.idUsuario,
        payload: publicationResponse,
      });
      setOpen(false);
      setInputText("");
      setLocation("");
      setSubmitError("");
      clearPhoto();
      localStorage.setItem("hideWelcomePublications", "true");
      if (typeof onPublished === "function") {
        await onPublished(publicationResponse);
      }
    } catch (error) {
      console.error("Failed attempting to publish ", error);
      setSubmitError(
        texts?.ER0058 || "No se pudo publicar. Intentalo de nuevo mas tarde.",
      );
    } finally {
      setLoading(false);
    }
  };

  const buildPostReq = () => {
    return {
      IdUsuario: userData?.idUsuario,
      Comentario: inputText.trim(),
      Ubicacion: location,
      UsuariosArrobados: mentionedUsers.length > 0 ? mentionedUsers : null,
      files: sendImg
        ? [
            {
              id: photoID,
              filename,
              filetype,
              base64,
            },
          ]
        : [],
    };
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    let isValid = false;
    if (file) isValid = beforeUpload(file);
    if (isValid) await savePhoto(file);
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) showWarningMessage(texts?.AL0024);
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) showWarningMessage(texts?.AL0025);
    return isJpgOrPng && isLt5M;
  };

  const savePhoto = async (file) => {
    setFilename(file.name);
    setFiletype(file.type);
    setSendImg(true);
    const photoGuid = generateGuid();
    setPhotoID(photoGuid);
    const reader = new FileReader();
    reader.onloadend = () => setBase64(reader.result);
    reader.readAsDataURL(file);
  };

  const onEmojiClick = (emojiData) => {
    setInputText((prevText) => {
      const nextValue = prevText + emojiData.emoji;
      if (nextValue.length > TEXT_LIMITS.max) return prevText;
      return nextValue;
    });
    if (submitError) setSubmitError("");
  };

  const resetField = () => {
    setOpen(false);
    setInputText("");
    setLocation("");
    setCurrentMentionSearch("");
    setCurrentMentionPrefix("@");
    setHashtagSuggestions([]);
    setSubmitError("");
    clearPhoto();
  };

  const clearPhoto = () => {
    setBase64("");
    setFilename("");
    setFiletype("");
    setPhotoID("");
    setSendImg(false);
  };

  useEffect(() => {
    const urls = inputText.match(/(https?:\/\/[^\s]+)/g);
    setDetectedUrl(urls?.[0]);
  }, [inputText]);

  const mentionOptions = filteredSuggestions.map((user) => ({
    key: user.idUsuario,
    value: user.nickName.replace("@", ""),
    label: (
      <div style={{ display: "flex", alignItems: "center" }}>
        <img
          src={getMentionPhoto(user)}
          alt={user.nombre}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = DEFAULT_PROFILE_IMAGE;
          }}
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            marginRight: 8,
            objectFit: "cover",
          }}
        />
        <div>
          <div>{user.nombre}</div>
          <div style={{ fontSize: 12, color: "#666" }}>{user.nickName}</div>
        </div>
      </div>
    ),
  }));

  const hashtagOptions = hashtagSuggestions.map((hashtag) => ({
    key: `hashtag-${hashtag}`,
    value: hashtag.replace(/^#/, ""),
    label: <div style={{ padding: "4px 0", fontWeight: 600 }}>{hashtag}</div>,
  }));

  const validation = getTextValidationState(inputText);
  const canPublish = sendImg ? !validation.isTooLong : validation.isValid;
  const mentionsClassName = [
    "post-mentions",
    submitError || validation.isTooLong || (!sendImg && validation.isEmpty)
      ? "is-error"
      : validation.isNearLimit
        ? "is-warning"
        : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <ModalLocation
        open={locationModal}
        setOpen={setLocationModal}
        location={location}
        setLocation={setLocation}
      />

      <Modal
        className="post-modal"
        open={open}
        onCancel={resetField}
        centered
        footer={null}
        closeIcon={<CloseOutlined className="post-close-icon" />}
        width={720}
      >
        <div className="post-header-new-modal">
          <div className="post-header-left">
            <AvatarLayout userData={userData} photo={userData?.profilePic} />
          </div>
        </div>

        <div className="post-editor">
          <Mentions
            className={mentionsClassName}
            prefix={["@", "#"]}
            autoSize={{ minRows: 6, maxRows: 12 }}
            maxLength={TEXT_LIMITS.max}
            value={inputText}
            onChange={onChangeInput}
            onSearch={(text, prefix) => {
              setCurrentMentionSearch(text);
              setCurrentMentionPrefix(prefix);
            }}
            filterOption={false}
            placeholder={texts?.LB0653 || "Que esta pasando hoy?"}
            options={currentMentionPrefix === "#" ? hashtagOptions : mentionOptions}
          />
          <div className="post-editor-meta">
            <TextLengthIndicator
              value={inputText}
              submitError={submitError}
              emptyMessage=""
            />
          </div>

          {detectedUrl && (
            <div className="post-link-preview">
              <MicrolinkCard url={detectedUrl} />
            </div>
          )}

          {sendImg && (
            <div className="upload-image-container">
              <button className="remove-photo" onClick={clearPhoto}>
                <CloseOutlined />
              </button>
              <img src={base64} alt={filename} className="uploaded-img" />
            </div>
          )}

          {location && <div className="post-location">{location}</div>}
        </div>

        <div className="post-bottom">
          <div className="post-add-bar">
            <span className="post-add-label"></span>

            <div className="post-actions">
              <input
                id="profile-pic-upload"
                type="file"
                accept="image/png, image/jpg, image/jpeg"
                onChange={handleFileUpload}
                alt="upload"
                className="hidden-input"
              />
              <label
                htmlFor="profile-pic-upload"
                className="icon-btn"
                title="Imagen"
              >
                <img src="/img/icons/place-icon.svg" alt="imagen" />
              </label>

              <button
                type="button"
                className="icon-btn"
                onClick={() => setLocationModal(true)}
                title="Ubicacion"
              >
                <img src="/img/icons/location-icon.svg" alt="ubicacion" />
              </button>

              <div className="emoji-wrapper">
                <Popover
                  trigger="click"
                  placement="top"
                  overlayClassName="emoji-popover"
                  autoAdjustOverflow={{ adjustX: true, adjustY: true }}
                  arrowPointAtCenter
                  destroyTooltipOnHide
                  fresh
                  content={
                    <div className="emoji-popover-content">
                      <EmojiPicker
                        onEmojiClick={onEmojiClick}
                        width={300}
                        height={350}
                      />
                    </div>
                  }
                  getPopupContainer={() => document.body}
                >
                  <button type="button" className="icon-btn" title="Emojis">
                    <img src="/img/icons/emojis.png" alt="emojis" />
                  </button>
                </Popover>
              </div>
            </div>
          </div>

          <Button
            type="primary"
            className="publish-button"
            onClick={onOkUpload}
            loading={loading}
            disabled={!canPublish}
          >
            {texts?.LB0064 || "Publish"}
          </Button>
        </div>
      </Modal>
    </>
  );
};
